import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { SidebarComponent } from "../../core/sidebar/sidebar.component";
import { RouterLink } from '@angular/router';
import { Ride } from '../../shared/models/ride-history/ride.model';
import { HttpService } from '../../service/http/http.service';
import { DatePipe } from '@angular/common';
import { DurationPipe } from '../../shared/pipes/duration.pipe';
import { DistanceConverterPipe } from '../../shared/pipes/distance-converter.pipe';
import { CommonModule } from '@angular/common';
import { environment } from '@environments/environment';
import { backendRestApis } from '@environments/backend-rest-apis';
import { MiniMapComponent } from "../../core/map/mini-map/mini-map.component";
import { RideFilterComponent } from './ride-filter/ride-filter.component';
import { RideFilter, SortOption } from './ride-filter/ride-filter.model';

@Component({
  selector: 'app-ride-history',
  imports: [
    SidebarComponent,
    RouterLink,
    DatePipe,
    DurationPipe,
    DistanceConverterPipe,
    CommonModule,
    MiniMapComponent,
    RideFilterComponent
  ],
  templateUrl: './ride-history.component.html',
  styleUrl: './ride-history.component.css'
})
/**
 * Komponenta za prikaz povijesti vožnji.
 * Dohvaća i prikazuje popis svih vožnji korisnika.
 */
export class RideHistoryComponent implements OnInit {
  
  private httpService = inject(HttpService);
  private baseUrl = `${environment.apiUrl}${backendRestApis.ride.getAllRidesWithOrderByDateDesc}`;

  private allRides = signal<Ride[]>([]);
 
  private activeFilter = signal<RideFilter>({
    sort: 'newest',
    dateFrom: null,
    dateTo: null,
    distanceMin: 0,
    distanceMax: 99999,
  });
  
    rides = computed(() => this.applyFilterAndSort(this.allRides(), this.activeFilter()));


    maxDistance = computed(() => {
    const all = this.allRides();
    if (!all.length) return 100;
    return Math.ceil(Math.max(...all.map(r => r.distance)) / 1000);
  });

  /**
   * Inicijalizira komponentu i dohvaća podatke o vožnjama.
   */
  ngOnInit(){
    const subscription = this.httpService.get<Ride[]>(this.baseUrl)
    .subscribe({
      next: (rideArray) => {
        this.allRides.set(rideArray as unknown as Ride[]);
        this.activeFilter.update(f => ({ ...f, distanceMax: this.maxDistance() }));
      }
    });

    this.httpService.unsubscribeWithDestroyRef(subscription);
  }

  // okida metodu kada se promijeni filter
  onFilterChange(filter: RideFilter): void {
    this.activeFilter.set(filter);
  }
 
  // Primjenjivanje filtera i sortiranja na popis vožnji
  private applyFilterAndSort(rides: Ride[], filter: RideFilter): Ride[] {
    let result = [...rides];
 
    if (filter.dateFrom) {
      const from = new Date(filter.dateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter(r => new Date(r.date) >= from);
    }
    if (filter.dateTo) {
      const to = new Date(filter.dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(r => new Date(r.date) <= to);
    }
 
    result = result.filter(r =>
      r.distance >= filter.distanceMin * 1000 &&
      r.distance <= filter.distanceMax * 1000
    );
 
    switch (filter.sort as SortOption) {
      case 'newest':
        result.sort((a, b) => +new Date(b.date) - +new Date(a.date));
        break;
      case 'oldest':
        result.sort((a, b) => +new Date(a.date) - +new Date(b.date));
        break;
      case 'distance-desc':
        result.sort((a, b) => b.distance - a.distance);
        break;
      case 'distance-asc':
        result.sort((a, b) => a.distance - b.distance);
        break;
      case 'avg-speed-desc':
        result.sort((a, b) => (b.distance/b.duration) - (a.distance/a.duration));
        break;
      case 'duration-desc':
        result.sort((a, b) => b.duration - a.duration);
        break;
    }
 
    return result;
  }


}
